<template>
  <div>
    <div class="salary-rules__rule-container">
      <p>
        Med ansættelsesskabeloner kan du sætte standardregler for vikarløn – fx hvilken ansættelse, lønkode og evt. omkostningssted, vikartimer skal udbetales på.
        Skabeloner skal tilføjes til både fast personale (fx Lærer, Pædagog) og timelønnede vikarer (fx Lærervikar, Pædagogvikar) for at kunne bruges i opsætningen.
        Du kan altid rette i vikarlønsforslag i efterbehandlingen.
      </p>
    </div>
    <Loader bgColor="#fff" v-if="isLoading"></Loader>
    <template v-else>
      <div class="do-table__filters">
        <div class="do-table__filters__left">
          <DoFiltersFilter
            title="Personer"
            :tags="personNames"
            :selectedTags="selectedNames"
            :numberOfSelectedTagsToShow="1"
            @select="toggleName"
            @clear="selectedNames = []">
          </DoFiltersFilter>
          <DoFiltersFilter
            title="Grupper"
            :tags="tags"
            :selectedTags="selectedTags"
            :numberOfSelectedTagsToShow="1"
            @select="toggleTag"
            @clear="selectedTags = []">
          </DoFiltersFilter>
          <DoFiltersFilter
            v-if="employmentTitles.length > 1"
            title="Ansættelser"
            :tags="employmentTitles"
            :selectedTags="selectedEmploymentTitles"
            :numberOfSelectedTagsToShow="1"
            @select="toggleEmploymentTitle"
            @clear="selectedEmploymentTitles = []">
          </DoFiltersFilter>
          <DoFiltersFilter
            v-if="organisations.length > 1"
            title="Organisationer"
            :tags="organisations"
            :selectedTags="selectedOrganisations"
            :numberOfSelectedTagsToShow="1"
            @select="selectedOrganisation"
            @clear="selectedOrganisations = []">
          </DoFiltersFilter>
          <span class="do-table__filters__add__explainer-text">Viser {{ filteredUsers.length }} af {{ users.length }} brugere</span>
        </div>
        <div class="do-table__filters__add">
          <DoEmploymentDropdown
            :employmentTypes="employmentTemplates"
            :selectedEmploymentIds="selectedEmploymentIds"
            @onTemplateCreated="onTemplateCreated"
            @onTemplateUpdated="onTemplateUpdated"
            @onTemplateDeleted="onTemplateDeleted"
            @select="onTemplateSelected"
          />
          <div class="do-spinner" v-if="isSubmitting"></div>
          <span v-else>Valgt {{
              selectedEmploymentIds.length
            }} {{ selectedEmploymentIds.length === 1 ? 'ansættelse' : 'ansættelser' }}</span>
        </div>
      </div>

      <SalarySettingsUsersTable
        :isSalaryIntegrationActive="isSalaryIntegrationActive"
        :filteredUsers="filteredUsers"
        :isImportOfEmployments="true"
        :employmentTemplates="employmentTemplates"
        :selectedEmploymentIds="selectedEmploymentIds"
        @toggleEmployment="toggleEmployment"
        @toggleAllEmployments="toggleAllEmployments"
        @editEmployment="$emit('editEmployment', $event)"
        @setTheEditUser="$emit('setTheEditUser', $event)"
      />
    </template>
  </div>
</template>

<script>
import DModal from '../../common/DModal.vue'
import Loader from '../../common/Loader.vue'
import SalarySettingsUsersTable from './SalarySettingsUsersOpusTable.vue'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import TaggingFilter from '../../Tagging/TaggingFilter.vue'
import DoEmploymentDropdown from './DoEmploymentDropdown.vue'
import docendo from '../../../docendo'

export default {
  components: {DoEmploymentDropdown, TaggingFilter, DoFiltersFilter, SalarySettingsUsersTable, Loader, DModal},
  props: {
    users: {
      default: () => []
    },
    employments: {
      default: () => []
    },
    tags: {
      default: () => []
    },
    isSalaryIntegrationActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      selectedTags: [],
      selectedEmploymentTitles: [],
      selectedOrganisations: [],
      selectedEmploymentIds: [],
      isSubmitting: false,
      selectedNames: []
    }
  },
  computed: {
    employmentTemplates () {
      return this.$store.getters.employmentTemplatesSorted
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    personNames () {
      const names = new Set()
      this.users.forEach(u => {
        if (u && u.name) names.add(u.name)
      })
      return Array.from(names).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    organisations () {
      const organisations = new Set()
      this.employments.forEach(employment => {
        if (employment.organisation) {
          organisations.add(employment.organisation)
        }
      })
      return Array.from(organisations).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },

    // NYT: unikke ansættelsestitler
    employmentTitles () {
      const titles = new Set()
      this.employments.forEach(e => {
        if (e.title) titles.add(e.title)
      })
      return Array.from(titles).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    hasEmploymentFilter () {
      return this.selectedOrganisations.length > 0 || this.selectedEmploymentTitles.length > 0
    },

    // Udvidet: filtrer nu både på organisationer og ansættelsestitler
    filteredEmployments () {
      const hasOrgFilter = this.selectedOrganisations.length > 0
      const hasTitleFilter = this.selectedEmploymentTitles.length > 0

      return this.employments.filter(e => {
        const orgOk = hasOrgFilter ? this.selectedOrganisations.indexOf(e.organisation) > -1 : true
        const titleOk = hasTitleFilter ? this.selectedEmploymentTitles.indexOf(e.title) > -1 : true
        return orgOk && titleOk
      })
    },

    filteredUsers () {
      const allEmploymentsForUser = (userId) => {
        return this.employments.filter(e => e.user_id === userId)
      }

      const matchedEmploymentsForUser = (userId) => {
        return this.filteredEmployments.filter(e => e.user_id === userId)
      }

      return this.users
        // 0) filter på navne
        .filter(user => {
          if (this.selectedNames.length > 0) {
            return this.selectedNames.indexOf(user.name) > -1
          } else {
            return true
          }
        })
        // 1) filter på tags (uændret)
        .filter(user => {
          if (this.selectedTags.length > 0) {
            return user.user_tags.some(tag => this.selectedTags.indexOf(tag) > -1)
          } else {
            return true
          }
        })
        // 2) filter personer på ansættelses-match
        .filter(user => {
          if (!this.hasEmploymentFilter) return true
          const emps = matchedEmploymentsForUser(user.id)
          return emps.length > 0
        })
        // 3) byg v_employments til visning
        .map(user => {
          const emps = this.hasEmploymentFilter
            ? matchedEmploymentsForUser(user.id)
            : allEmploymentsForUser(user.id)

          return {
            ...user,
            v_employments: emps.sort((a, b) => {
              const aTitle = a.source_title || a.title || ''
              const bTitle = b.source_title || b.title || ''
              return aTitle.localeCompare(bTitle, 'da-DK')
            })
          }
        })
    }
  },
  watch: {
    selectedNames () {
      this.selectedEmploymentIds = []
    },
    selectedTags () {
      this.selectedEmploymentIds = []
    },
    selectedEmploymentTitles () {
      this.selectedEmploymentIds = []
    },
    selectedOrganisations () {
      this.selectedEmploymentIds = []
    }
  },
  methods: {
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx > -1) {
        this.selectedNames.splice(idx, 1)
      } else {
        this.selectedNames.push(name)
      }
    },
    onTemplateSelected (employmentType) {
      this.isSubmitting = true
      const employmentTemplateId = employmentType.id
      const employmentIds = this.selectedEmploymentIds
      this.selectedEmploymentIds = []
      docendo.assignEmploymentTemplateToEmployment(this.schoolId, employmentTemplateId, employmentIds, success => {
        this.isSubmitting = false
        this.$store.dispatch('readEmployments')
      }, failure => {
        this.isSubmitting = false
      })
    },
    toggleEmployment (employment) {
      const id = employment.id
      const idx = this.selectedEmploymentIds.indexOf(id)
      if (idx > -1) {
        this.selectedEmploymentIds.splice(idx, 1)
      } else {
        this.selectedEmploymentIds.push(id)
      }
    },

    toggleAllEmployments () {
      // Saml alle synlige ansættelser (efter filtre)
      const visibleEmploymentIds = []
      this.filteredUsers.forEach(user => {
        user.v_employments.forEach(e => visibleEmploymentIds.push(e.id))
      })

      if (this.selectedEmploymentIds.length > 0) {
        // Fravælg alle synlige (kun dem der vises lige nu)
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => visibleEmploymentIds.indexOf(id) === -1)
      } else {
        // Vælg alle synlige
        this.selectedEmploymentIds = visibleEmploymentIds.slice()
      }
    },
    onTemplateCreated () {
      this.readEmploymentTemplates()
    },
    onTemplateUpdated () {
      this.readEmploymentTemplates()
    },
    onTemplateDeleted () {
      this.readEmploymentTemplates()
    },
    toggleTag (tag) {
      const idx = this.selectedTags.indexOf(tag)
      if (idx > -1) {
        this.selectedTags.splice(idx, 1)
      } else {
        this.selectedTags.push(tag)
      }
    },
    selectedOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) {
        this.selectedOrganisations.splice(idx, 1)
      } else {
        this.selectedOrganisations.push(organisation)
      }
    },

    // NYT: toggle for ansættelsestitel
    toggleEmploymentTitle (title) {
      const idx = this.selectedEmploymentTitles.indexOf(title)
      if (idx > -1) {
        this.selectedEmploymentTitles.splice(idx, 1)
      } else {
        this.selectedEmploymentTitles.push(title)
      }
    },

    onClose () {
      this.$emit('close')
    },
    readEmploymentTemplates () {
      this.$store.dispatch('readEmploymentTemplates')
    }
  },
  mounted () {
    this.readEmploymentTemplates()
  }
}
</script>

<style scoped>
.d-modal {
  width: calc(100vw - 3rem);
  height: calc(100vw - 3rem);
  border-radius: 16px;
  position: relative;
}

.d-modal__white {
  background: #fff;
}

.d-modal__header__custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem 0 1rem;
}

.do-table__filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  border-bottom: 0;
  padding: 8px;
  border-radius: 4px 4px 0 0;
}

.do-table__filters__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.do-table__filters__add {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.do-table__filters__add span,
.do-table__filters__add__explainer-text {
  font-style: italic;
  font-size: 11px;
}

.salary-rules__rule-container {
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  font-style: italic;
}
</style>



// WEBPACK FOOTER //
// SalarySettingsUsersOpus.vue?2a61fa06